import {
  createStrictContext,
  MayNull,
  PageInfo,
  useAccountContext
} from '@apeiron/temp'
import { GUILD_PER_PAGE } from '@src/constants/battleDemo'
import { GuildType } from '@src/constants/guild'
import useChangeGuild from '@src/hooks/graphql/useChangeGuild'
import useGetGuilds from '@src/hooks/graphql/useGetGuilds'
import useGetMyGuild from '@src/hooks/graphql/useGetMyGuild'
import { Guild } from '@src/types/guild'
import * as R from 'ramda'
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react'

const [ContextProvider, useGuildChangeContext] =
  createStrictContext<ContextProps>('GuildChange')

export { useGuildChangeContext }

export const GuildChangeProvider: FC<Props> = props => {
  const { children } = props

  const { isLoggedIn } = useAccountContext()

  const [guildType, setGuildType] = useState<MayNull<GuildType>>(null)

  const [selectedGuild, setSelectedGuild] = useState<MayNull<Guild>>(null)

  const {
    data: myGuild,
    fetch: fetchMyGuild,
    loading: fetchMyGuildLoading
  } = useGetMyGuild()

  const { mutate: doChangeGuild } = useChangeGuild()

  const {
    data: guilds,
    pageInfo: guildsPageInfo,
    fetch: fetchGuilds
  } = useGetGuilds()

  const changeGuild = useCallback(async () => {
    if (!R.isNil(selectedGuild) && !R.isNil(guildType)) {
      return doChangeGuild({
        id: selectedGuild.id,
        type: guildType
      })
    }

    return false
  }, [doChangeGuild, guildType, selectedGuild])

  const jumpToPage = useCallback(
    (page: number) => {
      if (R.isNotNil(guildType)) {
        fetchGuilds({
          pageInput: {
            page,
            pageSize: guildsPageInfo.pageSize
          },
          type: guildType
        })
      }
    },
    [fetchGuilds, guildType, guildsPageInfo.pageSize]
  )

  useEffect(() => {
    if (R.isNotNil(guildType)) {
      fetchGuilds({
        pageInput: {
          page: 1,
          pageSize: GUILD_PER_PAGE
        },
        type: guildType
      })

      if (isLoggedIn) {
        fetchMyGuild({
          type: guildType
        })
      }
    }
  }, [guildType, isLoggedIn])

  return (
    <ContextProvider
      value={{
        guildType,
        guilds,
        guildsPageInfo,
        myGuild: isLoggedIn ? myGuild : null,
        fetchMyGuildLoading,
        selectedGuild,
        changeGuild,
        jumpToPage,
        setGuildType,
        setSelectedGuild
      }}
    >
      {children}
    </ContextProvider>
  )
}

type ContextProps = {
  guildType: MayNull<GuildType>
  guilds: Guild[]
  guildsPageInfo: PageInfo
  myGuild: MayNull<Guild>
  fetchMyGuildLoading: boolean
  selectedGuild: MayNull<Guild>
  changeGuild: () => Promise<boolean>
  jumpToPage: (page: number) => void
  setGuildType: Dispatch<SetStateAction<MayNull<GuildType>>>
  setSelectedGuild: Dispatch<SetStateAction<MayNull<Guild>>>
}

type Props = {
  children: ReactNode
}
